import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LancamentosRejeitadosValorPre } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { normalizeGuiaMotivo } from '@gestor-web/app/shared/utils/guias.utils';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { LancamentosRejeitadosValorPreService } from './lancamentos-rejeitados-valor-preestabelecido.service';

const GRID_KEY = 'ANALISES.LANCAMENTOS_REJEITADOS_VALOR_PRE';

const MULTILINE_COLUMN: Pick<
  ColDef,
  'wrapText' | 'autoHeight' | 'cellClass' | 'filterValueGetter' | 'getQuickFilterText'
> = {
  wrapText: true,
  autoHeight: true,
  cellClass: 'cell-multiline',
  filterValueGetter: params => {
    const field = params.colDef.field;
    if (!field) return '';
    const value = (params.data as any)?.[field];
    return normalizeMultilineValue(value);
  },
  getQuickFilterText: params => normalizeMultilineValue(params.value),
};

const normalizeMultilineValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value
      .map(v => String(v).trim())
      .filter(Boolean)
      .join(' | ');
  }
  if (typeof value === 'string') {
    return value
      .replace(/\r?\n+/g, ' | ')
      .replace(/\s*\|\s*/g, ' | ')
      .trim();
  }
  if (value == null) return '';
  return String(value);
};

const COLUMNS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 140,
  },
  {
    headerName: '088-CNES Executante',
    field: 'nr_cnes_exec',
    width: 180,
  },
  {
    headerName: '089-Tipo Docto - CNPJ ou CPF',
    field: 'id_cnpj_cpf_exec',
    width: 160,
  },
  {
    headerName: '090-CNPJ/CPF Executante',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
    width: 200,
  },
  {
    headerName: '091-Município do Executante',
    field: 'cd_municipio_exec',
    width: 200,
  },
  {
    headerName: '092-Registro ANS da operadora intermediária',
    field: 'nr_registro_ans_intermediaria',
    width: 200,
  },
  {
    headerName: '093-Nr. Contrato Valor Pré',
    field: 'nr_contrato_valor_pre',
    width: 200,
  },
  {
    headerName: '119-Competência da cobertura contratada',
    field: 'nr_competencia_cobertura',
    type: 'competencia',
    width: 190,
  },
  {
    headerName: '094-Valor Cobertura na Competência',
    field: 'vl_cobertura',
    type: 'money',
    width: 180,
  },
  {
    headerName: 'Nome arquivo no Gestor IDSS',
    field: 'nm_arquivo',
    width: 200,
  },
  {
    headerName: 'Rejeição do arquivo',
    field: 'motivo_arquivo',
    width: 240,
  },
  {
    headerName: 'Rejeição do campo',
    field: 'lt_campo_guia',
    width: 240,
    ...MULTILINE_COLUMN,
  },
  {
    headerName: 'Rejeição da guia',
    field: 'lt_motivo_guia',
    width: 240,
    ...MULTILINE_COLUMN,
  },
];

@Component({
    templateUrl: './lancamentos-rejeitados-valor-preestabelecido.component.html',
    styleUrls: ['./lancamentos-rejeitados-valor-preestabelecido.component.scss'],
    providers: [LancamentosRejeitadosValorPreService, DecimalPipe],
    standalone: false
})
export class LancamentosRejeitadosValorPreComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<LancamentosRejeitadosValorPre>;

  gridOptions: GridOptions<LancamentosRejeitadosValorPre>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];
  appliedCompetenciaRange: string[] = [];

  rowData: LancamentosRejeitadosValorPre[] = [];

  pageTooltip = 'RELATORIOS_PERSONALIZADOS.LANCAMENTOS_REJEITADOS_VALOR_PREESTABELECIDO';

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  private ngDestroyed$ = new Subject<void>();

  constructor(
    private permissaoService: PermissaoService,
    private service: LancamentosRejeitadosValorPreService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private competenciaPipe: CompetenciaPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : this.competenciaRange[0],
          params.competencia_final ? '' + params.competencia_final : this.competenciaRange[1],
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<LancamentosRejeitadosValorPre>(
      {
        columnDefs: COLUMNS,
        pipes: {
          date: this.datePipe,
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  resetPagination() {
    this.filterChanged = true;
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.resetPagination();
  }

  reloadData(applyPendingFilters: boolean = false) {
    const shouldApplyFilters = applyPendingFilters || !this.appliedCompetenciaRange?.length;
    if (shouldApplyFilters) {
      this.appliedCompetenciaRange = [...this.competenciaRange];
      this.filterChanged = false;
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => this.agGrid?.api?.setGridOption('loading', true));
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.appliedCompetenciaRange[0] || !this.appliedCompetenciaRange[1]) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('loading', false);
      return;
    }

    this.service
      .getDataGoApi(this.operadora, this.appliedCompetenciaRange[0], this.appliedCompetenciaRange[1])
      .subscribe({
        next: ({ items }) => {
          this.rowData = (items ?? []).map(item => this.normalizeRow(item));
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGrid?.api?.setGridOption('loading', false),
      });
  }

  private normalizeRow(item: LancamentosRejeitadosValorPre): LancamentosRejeitadosValorPre {
    return {
      ...item,
      lt_motivo_guia: normalizeGuiaMotivo(item.lt_motivo_guia),
    };
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const competencia = this.appliedCompetenciaRange?.length ? this.appliedCompetenciaRange : this.competenciaRange;
    const fileNamePrefix = `lancamentos-rejeitados-valor-pre-${this.operadoraNome}-${competencia[0]}-${competencia[1]}`;

    const headers = [
      'Lançamentos Rejeitados de Valor Preestabelecido',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(competencia),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);
    const competencia = this.appliedCompetenciaRange?.length ? this.appliedCompetenciaRange : this.competenciaRange;

    const urlFilter = {
      competencia: competencia[0],
      competencia_final: competencia[1],
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(urlFilter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}
